/**
 * NetworkDashboardGrid — the rich, alive Networks landing grid (LANDING).
 *
 * Renders NetworkStatTile in a 3-col grid, 9/page, with a client search filter.
 * Clicking a tile's "View rooms" expands an inline accordion (RoomStatRow list,
 * lazily fetched + paginated 9). Live vitals come from `useDashboardData`
 * (poll + coarse per-user beacon). The ⋮ actions + open behavior are injected by
 * the host (AppBrowsePage) so the existing Networks-tab handlers are reused.
 */
import React, { useMemo, useState, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, ChevronLeft, ChevronRight, Plus, Boxes, Users, Activity, Server, Check, X } from 'lucide-react';
import { getRoomsDashboard } from '@stepflow/ui-shared/services/networksDashboardApi';
import { ConsumptionMeter } from '@stepflow/ui-shared/components/dashboard/vitalWidgets.jsx';
import NetworkStatTile from './NetworkStatTile.jsx';
import RoomStatRow from './RoomStatRow.jsx';

const PAGE_SIZE = 9;
const ROOMS_PAGE_SIZE = 9;

const ACTIVE_SPARSE_MAX = 3;   // ≤ this many active → also show Deployed below a divider

const isActiveNet = (n) => Number(n && n.roomCount || 0) > 0;

export default function NetworkDashboardGrid({
  networks,          // networkVitals[] (for 'active'/'all' the FULL set; else the slice)
  loading,
  error,
  query = '',
  livePing = {},
  toneById = {},     // networkId -> 'green' | 'yellow'  (config-status tint)
  lifecycleFilter,   // 'active' | 'deployed' | 'all' | … — drives grouping + build-new tile
  onBuildNew,        // () => void — the '+ Build new' tile (Deployed view)
  onOpenNetwork,     // (vitals) => void
  onOpenRoom,        // (networkId, poolId) => void
  renderActions,     // (vitals) => ReactNode  (the ⋮ menu)
  emptyHint,         // host-supplied text for the no-networks (no-query) case
}) {
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  // Footer consumption unit toggle ($ / pts) — emulates the per-tile toggle.
  const [footerUnit, setFooterUnit] = useState('usd');
  // networkId -> { rooms, total, offset, loading, error }
  const [roomsById, setRoomsById] = useState({});
  const loadingRef = useRef(new Set());

  // Is a network "running right now"? Either the live AppSync beacon flipped it
  // (livePing, ~1s) or the last poll/refetch already reported active rooms.
  const isRunning = useCallback((n) => (
    livePing[n.networkId] === 'RUNNING' || Number(n.activeRoomCount || 0) > 0
  ), [livePing]);

  // Build the ordered list of RENDER ITEMS — networks interleaved with a divider
  // and/or a build-new tile, depending on the lifecycle filter. Each item is
  // ``{type:'net', net}`` | ``{type:'divider', label}`` | ``{type:'build-new'}``.
  // Search + running-first sort apply throughout; the pagination below is over
  // this flat item list so a page never splits a group oddly.
  const items = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    const match = (n) => !q
      || String(n.name || '').toLowerCase().includes(q)
      || String(n.networkId || '').toLowerCase().includes(q);
    // Stable running-first ordering within any group (a live run floats to #1).
    const rank = (n) => (isRunning(n) ? 0 : 1);
    const sortRunning = (arr) => [...arr].sort((a, b) => rank(a) - rank(b));

    const matched = (networks || []).filter(match);
    const active = sortRunning(matched.filter(isActiveNet));
    const deployed = sortRunning(matched.filter((n) => !isActiveNet(n)));
    const netItem = (net) => ({ type: 'net', net, key: net.networkId });
    const out = [];

    if (lifecycleFilter === 'deployed') {
      // Deployed view: a first-class "+ Build new" tile, then the no-room networks.
      if (onBuildNew) out.push({ type: 'build-new', key: '__build_new__' });
      out.push(...deployed.map(netItem));
      return out;
    }
    if (lifecycleFilter === 'active') {
      out.push(...active.map(netItem));
      // Sparse active set → show the Deployed ones below a divider so the view
      // isn't barren. (Only when there actually ARE deployed networks to show.)
      if (active.length <= ACTIVE_SPARSE_MAX && deployed.length) {
        out.push({ type: 'divider', label: 'Deployed', key: '__divider_deployed__' });
        out.push(...deployed.map(netItem));
      }
      return out;
    }
    // 'all' (and any non-lifecycle filter that passed a pre-sliced set): Active
    // first, then Deployed — each group running-first.
    out.push(...active.map(netItem));
    out.push(...deployed.map(netItem));
    return out;
  }, [networks, query, isRunning, lifecycleFilter, onBuildNew]);

  const total = items.length;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const safePage = Math.min(page, maxPage);
  const pageItems = items.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  // Cumulative "fleet" totals across the CURRENTLY-FILTERED networks (all pages,
  // not just this page) — the footer's at-a-glance summary of what's on screen.
  const fleet = useMemo(() => {
    const nets = items.filter((it) => it.type === 'net').map((it) => it.net);
    const t = {
      count: nets.length, rooms: 0, agents: 0, activeRooms: 0,
      runsTotal: 0, runsSucceeded: 0, usd: 0, points: 0,
    };
    for (const n of nets) {
      t.rooms += Number(n.roomCount || 0);
      t.agents += Number(n.totalAgents ?? (Number(n.agentCount || 0) * Number(n.roomCount || 0)));
      t.activeRooms += Number(n.activeRoomCount || 0);
      t.runsTotal += Number(n.runsTotal || 0);
      t.runsSucceeded += Number(n.runsSucceeded || 0);
      t.usd += Number(n.consumedUsd || 0);
      t.points += Number(n.consumedPoints || 0);
    }
    t.runsFailed = Math.max(0, t.runsTotal - t.runsSucceeded);
    t.successPct = t.runsTotal > 0 ? Math.round((100 * t.runsSucceeded) / t.runsTotal) : null;
    return t;
  }, [items]);

  const loadRooms = useCallback(async (networkId, offset = 0) => {
    if (loadingRef.current.has(networkId)) return;
    loadingRef.current.add(networkId);
    setRoomsById((m) => ({ ...m, [networkId]: { ...(m[networkId] || {}), loading: true, error: null } }));
    try {
      const res = await getRoomsDashboard(networkId, { offset, limit: ROOMS_PAGE_SIZE });
      setRoomsById((m) => ({
        ...m,
        [networkId]: {
          rooms: res.rooms, total: res.total, offset: res.offset,
          hasMore: res.hasMore, loading: false, error: null,
        },
      }));
    } catch (e) {
      setRoomsById((m) => ({
        ...m, [networkId]: { ...(m[networkId] || {}), loading: false, error: e?.message || 'Failed' },
      }));
    } finally {
      loadingRef.current.delete(networkId);
    }
  }, []);

  const toggleExpand = useCallback((v) => {
    const nid = v.networkId;
    setExpandedId((cur) => {
      const next = cur === nid ? null : nid;
      if (next && !roomsById[nid]) loadRooms(nid, 0);
      return next;
    });
  }, [roomsById, loadRooms]);

  if (loading && networks.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-xs text-gray-400" data-testid="network-dashboard-loading">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading networks…
      </div>
    );
  }
  if (error && networks.length === 0) {
    return <p className="py-8 text-center text-xs text-red-500">{error}</p>;
  }
  if (total === 0) {
    // A search miss is always about the query. Otherwise the host can supply a
    // context-specific hint (e.g. the "Pinned" filter with nothing pinned yet).
    if (query) {
      return <p className="py-8 text-center text-xs text-gray-400">No networks match “{query}”.</p>;
    }
    return (
      <p className="py-8 text-center text-xs text-gray-400" data-testid="network-dashboard-empty">
        {emptyHint || 'No networks yet.'}
      </p>
    );
  }

  return (
    // Fixed-height flex column: the TILES scroll (flex-1 min-h-0 overflow-y-auto),
    // the footer stays PINNED (shrink-0) — the scrollbar is scoped to the tiles
    // and never moves the footer bar.
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {pageItems.map((item) => {
          // A full-width labeled divider between the Active group and the Deployed
          // ones shown below it (sparse-active fill).
          if (item.type === 'divider') {
            return (
              <div key={item.key} className="sm:col-span-2 md:col-span-3"
                   data-testid="network-dashboard-divider">
                <div className="flex items-center gap-2 pt-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                  <span className="h-px flex-1 bg-gray-200" aria-hidden />
                  {item.label}
                  <span className="h-px flex-1 bg-gray-200" aria-hidden />
                </div>
              </div>
            );
          }
          // "+ Build new" tile — starts the create-a-network flow.
          if (item.type === 'build-new') {
            return (
              <button
                key={item.key}
                type="button"
                onClick={onBuildNew}
                data-testid="network-dashboard-build-new"
                className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50/40 p-3 text-sm font-medium text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50/40 hover:text-blue-600"
              >
                <Plus className="h-6 w-6" aria-hidden />
                Build new
              </button>
            );
          }
          const v = item.net;
          const expanded = expandedId === v.networkId;
          const rd = roomsById[v.networkId];
          return (
            <div key={v.networkId} className={expanded ? 'sm:col-span-2 md:col-span-3' : ''}>
              <NetworkStatTile
                vitals={v}
                expanded={expanded}
                justLive={livePing[v.networkId] === 'RUNNING'}
                tone={toneById[v.networkId] || null}
                onToggleExpand={toggleExpand}
                onOpen={onOpenNetwork}
                actionsMenu={renderActions ? renderActions(v) : null}
              />
              <AnimatePresence initial={false}>
                {expanded ? (
                  <motion.div
                    layout
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                    data-testid={`network-rooms-expansion-${v.networkId}`}
                  >
                    <div className="mt-2 space-y-1.5 rounded-lg border border-gray-100 bg-gray-50/60 p-2">
                      {rd?.loading && !rd?.rooms ? (
                        <div className="flex items-center gap-2 py-3 text-[11px] text-gray-400">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading rooms…
                        </div>
                      ) : rd?.error ? (
                        <p className="py-2 text-center text-[11px] text-red-500">{rd.error}</p>
                      ) : (rd?.rooms || []).length === 0 ? (
                        <p className="py-2 text-center text-[11px] text-gray-400">No rooms.</p>
                      ) : (
                        (rd.rooms).map((room) => (
                          <RoomStatRow
                            key={room.poolId}
                            room={room}
                            networkId={v.networkId}
                            unit={footerUnit}
                            onOpen={onOpenRoom}
                          />
                        ))
                      )}
                      {/* Room pagination (prev/next over the server offset). */}
                      {rd && rd.total > ROOMS_PAGE_SIZE ? (
                        <div className="flex items-center justify-between px-1 pt-1 text-[10px] text-gray-500">
                          <button type="button" disabled={rd.offset <= 0}
                            onClick={() => loadRooms(v.networkId, Math.max(0, rd.offset - ROOMS_PAGE_SIZE))}
                            className="rounded px-1.5 py-0.5 hover:bg-gray-100 disabled:opacity-40">
                            <ChevronLeft className="inline h-3 w-3" /> Prev
                          </button>
                          <span className="tabular-nums">
                            {rd.offset + 1}–{Math.min(rd.offset + ROOMS_PAGE_SIZE, rd.total)} of {rd.total}
                          </span>
                          <button type="button" disabled={!rd.hasMore}
                            onClick={() => loadRooms(v.networkId, rd.offset + ROOMS_PAGE_SIZE)}
                            className="rounded px-1.5 py-0.5 hover:bg-gray-100 disabled:opacity-40">
                            Next <ChevronRight className="inline h-3 w-3" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      </div>{/* end scrollable tiles region */}

      {/* Fixed FLEET-SUMMARY footer — a non-scrolling flex sibling (shrink-0), so
          the tiles scroll ABOVE it and this bar stays put. Cumulative vitals across
          the filtered networks (all pages) on the left, pagination on the right. */}
      <div className="flex shrink-0 items-center gap-3 border-t border-gray-100 px-5 py-3"
           data-testid="network-dashboard-footer">
        {/* Left: cumulative stats — same visual language as the tiles. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600">
          <span className="font-medium text-gray-500" data-testid="fleet-count">
            {fleet.count} network{fleet.count === 1 ? '' : 's'}
          </span>
          <span className="flex items-center gap-1" title="Rooms (total)">
            <Boxes className="h-3.5 w-3.5 text-blue-500" aria-hidden />
            <span className="tabular-nums" data-testid="fleet-rooms">{fleet.rooms}</span> rooms
          </span>
          <span className="flex items-center gap-1" title="Agents (total across rooms)">
            <Users className="h-3.5 w-3.5 text-indigo-500" aria-hidden />
            <span className="tabular-nums" data-testid="fleet-agents">{fleet.agents}</span> agents
          </span>
          <span className={`flex items-center gap-1 ${fleet.activeRooms > 0 ? 'text-emerald-600' : ''}`}
                title="Rooms running now">
            <Activity className="h-3.5 w-3.5" aria-hidden />
            <span className="tabular-nums" data-testid="fleet-active">{fleet.activeRooms}</span> active
          </span>
          {/* Runs — absolute breakdown (succeeded · failed · total) with color, so
              the raw error count is visible at a glance, not just a %. Below the
              numbers, a stacked green/red bar shows the success:fail proportion.
              The % is health-tinted (green ≥90 · amber ≥70 · red below). */}
          {fleet.runsTotal > 0 ? (
            <span className="flex flex-col gap-0.5" title="Runs across the shown networks (this period)"
                  data-testid="fleet-runs">
              <span className="flex items-center gap-2">
                <span className="flex items-center gap-0.5 text-emerald-600" title="Succeeded">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  <span className="tabular-nums" data-testid="fleet-succeeded">{fleet.runsSucceeded}</span>
                </span>
                <span className={`flex items-center gap-0.5 ${fleet.runsFailed > 0 ? 'text-red-600' : 'text-gray-300'}`}
                      title="Failed">
                  <X className="h-3.5 w-3.5" aria-hidden />
                  <span className="tabular-nums" data-testid="fleet-failed">{fleet.runsFailed}</span>
                </span>
                <span className="text-gray-400">/ <span className="tabular-nums" data-testid="fleet-runs-total">{fleet.runsTotal}</span></span>
                <span className={`font-semibold tabular-nums ${
                  fleet.successPct >= 90 ? 'text-emerald-600'
                    : fleet.successPct >= 70 ? 'text-amber-600' : 'text-red-600'
                }`} data-testid="fleet-success-pct">{fleet.successPct}%</span>
              </span>
              {/* Success : fail proportion bar. */}
              <span className="flex h-1.5 w-full min-w-[80px] overflow-hidden rounded-full bg-gray-200"
                    data-testid="fleet-runs-bar" aria-hidden>
                <span className="h-full bg-emerald-500" style={{ width: `${fleet.successPct}%` }} />
                <span className="h-full bg-red-500" style={{ width: `${100 - fleet.successPct}%` }} />
              </span>
            </span>
          ) : (
            <span className="text-gray-400" data-testid="fleet-runs">no runs</span>
          )}
          {/* Cumulative consumption — emulates the tile: meter + a $ / pts unit
              dropdown (no "consumed" text). */}
          <span className="flex items-center gap-1"
                title={footerUnit === 'usd' ? 'Estimated cost (this month)' : 'Points consumed (this month)'}
                data-testid="fleet-consumption">
            <Server className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
            <ConsumptionMeter unit={footerUnit} consumed={fleet.points} consumedUsd={fleet.usd} budget={0} hideUnit />
            <select
              value={footerUnit}
              onChange={(e) => setFooterUnit(e.target.value)}
              aria-label="Consumption unit"
              title="Show cost in $ or points"
              data-testid="fleet-unit"
              className="shrink-0 cursor-pointer rounded border-0 bg-transparent py-0 pl-0.5 pr-3 text-[10px] text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-0"
            >
              <option value="usd">$</option>
              <option value="points">pts</option>
            </select>
          </span>
        </div>

        {/* Right: pagination (only when multi-page; the stats always show). */}
        {maxPage > 0 ? (
          <div className="flex shrink-0 items-center gap-2 text-[11px] text-gray-500">
            <button type="button" disabled={safePage <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="network-dashboard-prev"
              className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 hover:bg-gray-50 disabled:opacity-40">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="tabular-nums">Page {safePage + 1} / {maxPage + 1}</span>
            <button type="button" disabled={safePage >= maxPage}
              onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
              data-testid="network-dashboard-next"
              className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 hover:bg-gray-50 disabled:opacity-40">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
